import React, { useState, useEffect } from 'react';
import { Camera, X, Plus, Trash2 } from 'lucide-react';
import { getTemporaryPhotos, clearTemporaryPhotos } from '../services/photoService';
import CameraComponent from './CameraComponent';
import ConfirmDialog from './ConfirmDialog';

interface TemporaryPhotoComponentProps {
  onClose?: () => void;
}

const TemporaryPhotoComponent: React.FC<TemporaryPhotoComponentProps> = ({ onClose }) => {
  const [tempPhotos, setTempPhotos] = useState<Array<{
    id: string;
    dataUrl: string;
    notes: string;
    takenAt: number;
  }>>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Load temporary photos from localStorage
  useEffect(() => {
    const loadTempPhotos = () => {
      const photos = getTemporaryPhotos();
      setTempPhotos(photos);
    };
    
    loadTempPhotos();
  }, []);

  // Handle photo taken
  // Fires per saved photo while the camera stays open for the next shot.
  const handlePhotoTaken = (photoId: string) => {
    setTempPhotos(getTemporaryPhotos());
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setTempPhotos(getTemporaryPhotos());
  };

  // Delete a temporary photo
  const handleDeletePhoto = (photoId: string) => {
    // Get current photos
    const currentPhotos = getTemporaryPhotos();
    
    // Filter out the photo to delete
    const updatedPhotos = currentPhotos.filter(photo => photo.id !== photoId);
    
    // Save back to localStorage
    localStorage.setItem('temporaryPhotos', JSON.stringify(updatedPhotos));
    
    // Update state
    setTempPhotos(updatedPhotos);
    setShowConfirmDelete(null);
  };

  // Clear all temporary photos
  const handleClearAll = () => {
    setShowClearConfirm(true);
  };

  const confirmClearAll = () => {
    clearTemporaryPhotos();
    setTempPhotos([]);
    setShowClearConfirm(false);
  };

  // Format date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('sv-SE') + ' ' + date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-surface rounded-lg shadow-md p-3 sm:p-4">
      <ConfirmDialog
        isOpen={showClearConfirm}
        title="Rensa alla"
        message="Är du säker på att du vill ta bort alla tillfälliga foton?"
        confirmLabel="Rensa alla"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmClearAll}
        onCancel={() => setShowClearConfirm(false)}
      />
      {showCamera ? (
        <CameraComponent 
          projectId="temp" // This will be replaced when the project is created
          onPhotoTaken={handlePhotoTaken}
          onClose={handleCameraClose}
        />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-on-surface">Tillfälliga foton</h2>
            <div className="flex flex-wrap gap-2">
              {tempPhotos.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1 min-w-[100px] justify-center"
                >
                  <Trash2 size={16} />
                  <span>Rensa alla</span>
                </button>
              )}
              <button
                onClick={() => setShowCamera(true)}
                className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1 min-w-[100px] justify-center"
              >
                <Camera size={16} />
                <span>Ta foto</span>
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="bg-on-surface-secondary hover:bg-on-surface text-white p-2 rounded-md flex items-center justify-center"
                  aria-label="Stäng"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
          
          {tempPhotos.length === 0 ? (
            <div className="text-center py-8 text-on-surface-tertiary">
              <p>Inga tillfälliga foton ännu.</p>
              <p className="mt-2 text-sm">Ta foton nu och koppla dem till projektet senare.</p>
              <button
                onClick={() => setShowCamera(true)}
                className="mt-4 bg-surface-alt hover:bg-surface-hover text-on-surface-secondary px-4 py-2 rounded-md flex items-center gap-2 mx-auto"
              >
                <Camera size={18} />
                <span>Ta första fotot</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {tempPhotos.map(photo => (
                <div key={photo.id} className="border border-theme-border rounded-md overflow-hidden bg-surface-alt">
                  <div className="relative">
                    <img 
                      src={photo.dataUrl} 
                      alt="Tillfälligt foto" 
                      className="w-full h-40 sm:h-48 object-cover"
                    />
                    <button
                      onClick={() => setShowConfirmDelete(photo.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                      aria-label="Ta bort foto"
                    >
                      <Trash2 size={16} />
                    </button>
                    
                    {showConfirmDelete === photo.id && (
                      <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4">
                        <div className="bg-surface p-4 rounded-md max-w-xs w-full">
                          <p className="text-on-surface mb-4">Är du säker på att du vill ta bort detta foto?</p>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setShowConfirmDelete(null)}
                              className="bg-surface-alt text-on-surface px-3 py-2 rounded-md"
                            >
                              Avbryt
                            </button>
                            <button
                              onClick={() => handleDeletePhoto(photo.id)}
                              className="bg-red-500 text-white px-3 py-2 rounded-md"
                            >
                              Ta bort
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-on-surface-tertiary">{formatDate(photo.takenAt)}</p>
                    {photo.notes && (
                      <p className="text-sm text-on-surface-secondary mt-1">{photo.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemporaryPhotoComponent; 